import { Component, OnInit, Input, OnDestroy, ElementRef } from '@angular/core';
import { animate, style, transition, trigger, state } from '@angular/animations';

@Component({
  selector: 'nc-number-counter',
  templateUrl: './number-counter.component.html',
  styleUrls: ['./number-counter.component.less'],
  animations: [
    trigger('countAnimation', [
      state('initial', style({
        transform: 'translateY(0)'
      })),
      state('final', style({
        transform: 'translateY({{finalPosition}}em)'
      }), {
        params: { finalPosition: 0 }
      }),
      transition('initial => final', [
        animate('{{duration}}ms {{delay}}ms cubic-bezier(0.33, 1, 0.68, 1)')
      ], {
        params: { duration: 1000, delay: 0 }
      })
    ])
  ]
})
export class NumberCounterComponent implements OnInit, OnDestroy {
  /** 要显示的数字 */
  private _value: number = 0;
  @Input()
  set ncValue(value: number) {
    this._value = value;
    setTimeout(() => {
      this.start();
    });
  }
  get ncValue(): number {
    return this._value;
  }

  /** 每个数字动画持续时间 */
  @Input() ncDuration: number = 1000;
  /** 每个数字之间的延迟 */
  @Input() ncDelayMultiple: number = 0;
  /** 存在每个数字之间的延迟时，数字方向 */
  @Input() ncDirection: 'left' | 'right' = 'left';
  /** 每个数字之间的间距 */
  @Input() ncGap: number = 0;
  /** 开始前的延迟 */
  @Input() ncDelay: number = 0;
  /** 是否在视图可见时才开始动画 */
  private _startOnView: boolean = false;
  @Input()
  set ncStartOnView(val: boolean | string) {
    this._startOnView = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncStartOnView(): boolean {
    return this._startOnView;
  }
  /** 是否只执行一次动画 */
  private _once: boolean = false;
  @Input()
  set ncOnce(val: boolean | string) {
    this._once = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncOnce(): boolean {
    return this._once;
  }

  digits: { numbers: number[], targetPosition: number, state: string, delay: number }[] = [];
  private observer: IntersectionObserver | null = null;
  private timeout: any = null;

  constructor(private elementRef: ElementRef) { }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this.clear();
  }

  private start(): void {
    this.clear();
    if (!this.ncStartOnView) {
      this.processValue();
      this.timeout = setTimeout(() => {
        this.startAnimation();
      }, this.ncDelay);
    } else {
      this.setupIntersectionObserver();
    }
  }

  private clear(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.processValue();
        this.timeout = setTimeout(() => {
          this.startAnimation();
        }, this.ncDelay);

        if (this.ncOnce) {
          if (this.observer) {
            this.observer.disconnect();
          }
        }
      } else if (!this.ncOnce) {
        this.resetAnimation();
      }
    },
      { threshold: 0.1 }
    );

    this.observer.observe(this.elementRef.nativeElement);
  }

  private processValue(): void {
    this.digits = [];
    const stringValue = this.ncValue.toString();

    for (let i = 0; i < stringValue.length; i++) {
      const digit = parseInt(stringValue[i]);

      // 对于所有数字，都生成0到9的序列，然后再从0到最终数字
      let numbers = [
        ...Array.from({ length: 10 }, (_, index) => index),
        ...Array.from({ length: digit + 1 }, (_, index) => index)
      ];

      let delay = 0;
      if (this.ncDirection === 'left') {
        delay = (stringValue.length - 1 - i) * this.ncDelayMultiple;
      } else {
        delay = i * this.ncDelayMultiple;
      }
      
      this.digits.push({
        numbers: numbers,
        targetPosition: -(numbers.length - 1),
        state: 'initial',
        delay: delay
      });
    }
  }

  private startAnimation(): void {
    this.digits.forEach((digit) => {
      setTimeout(() => {
        digit.state = 'final';
      }, digit.delay);
    });
  }

  private resetAnimation(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.digits.forEach(digit => {
      digit.state = 'initial';
    });
  }
}
